import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  useUpdateFrameMutation,
  useDeleteFrameMutation,
  useDuplicateFrameMutation,
  useGetElearningQuery,
  useGetFramesQuery,
  useCreateElementMutation,
  useTweakSlideMutation,
  useRegeneratePptxSlideMutation,
} from '@/modules/services';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';

import {
  Alert,
  Button,
  Col,
  CopyIcon,
  DeleteOutlinedIcon,
  Dialog,
  Link,
  MoreHorizIcon,
  Popover,
  Row,
} from 'elmo-elements';

import ContainerForm from '@/components/ContainerForm';

import Content from '../Content';
import Comments from '../Comments';
import Tooltips from '../Tooltips';
import SlideSetupComponent from '../SlideSetup';
import { FrameDataType, VocamMetadataType } from '@/modules/types/frame';
import ModuleRoutes, {
  ElearningParamType,
  ModuleSlideRoutes,
} from '@/pages/Module/routes';
import { FrameQueryType } from '@/modules/services/frames';
import {
  getCkFileType,
  getCkFinderConfig,
  getModuleFormData,
  hasValidVocamDataIds,
  removeHashOnHex,
} from '@/lib/util';
import VideoSlideSetupComponent from '../VideoSlideSetup';
import { CkFinderResourceTypeEnum } from '@/modules/types/ckFinder';
import { browseAssets } from '@/components/CKFileUpload';
import { NavigatePrompt } from '@/components/NavigatePrompt/NavigatePrompt';
import { useFrameStyle } from '@/modules/hooks/useFrameStyle';
import { useAiFeatureFlags } from '@/lib/featureFlags/useAiFeatureFlags';
import {
  AI_EDIT_BUSY_MESSAGE,
  getMonthlyCapMessage,
  HIGH_DEMAND_MESSAGE,
  PPT_IMPORT_HELP_URL,
  REGENERATE_MODES,
  SLIDE_EDIT_TIMEOUT_MESSAGE,
  SLIDE_REPLACE_TIMEOUT_MESSAGE,
} from '@/modules/services/pptImport';
import RegenerateSlideModal from './RegenerateSlideModal';
import KeepAsImageModal from './KeepAsImageModal';

// A gateway 504 arrives as an HTML page, which RTK Query reports as a PARSING_ERROR
// carrying originalStatus. A FETCH_ERROR is left out on purpose: the request may
// never have reached the API (offline, DNS), so "it may still appear" would mislead.
const isSlideEditTimeout = (err: any): boolean =>
  err?.status === 504 || err?.originalStatus === 504;

// The high-demand copy for a rate limit (AI_RATE_LIMITED) or any other 503, such as
// guardrail screening being unavailable. Only the API's own rate-limit body is shown;
// something in front of the API could word a 503 for engineers.
const getHighDemandMessage = (err: any): string | null => {
  if (err?.data?.error === 'AI_RATE_LIMITED') {
    return err?.data?.message ?? HIGH_DEMAND_MESSAGE;
  }
  if (err?.status === 503 || err?.originalStatus === 503) {
    return HIGH_DEMAND_MESSAGE;
  }
  return null;
};

// The copy for a slide edit the gateway gave up on. The API finishes the edit anyway,
// so the admin reloads to see it: a placeholder is replaced in place, any other edit
// adds a new slide.
const getSlideEditTimeoutMessage = (replacesInPlace: boolean): string =>
  replacesInPlace ? SLIDE_REPLACE_TIMEOUT_MESSAGE : SLIDE_EDIT_TIMEOUT_MESSAGE;

// Provisional copy (LMS2-5106): Design owns the final wording and placement.
const BLOCKED_INSTRUCTION_MESSAGE =
  "We can't apply this instruction. Some of what you've asked for isn't supported \u2014 revise it and try again.";

export const SLIDE_OPS = {
  EDIT_AI: 'edit-ai',
  KEEP_AS_IMAGE: 'keep-as-image',
} as const;

export type SlideOp = (typeof SLIDE_OPS)[keyof typeof SLIDE_OPS];

export interface ContentFormType {
  id: string;
  elearningId: string;
  title: string;
  content: string;
  mobileContent: string;
  bgVideo: string;
  bgVideoTitle: string;
  isVideoFrame: boolean;
  frameMetadata: {
    backgroundColor?: string | null;
    bgImage?: string | null;
    bgVideo?: string | null;
    mute?: number | null;
    sound?: string | null;
    swipeNavigation?: number | null;
    position?: number | null;
    disabled?: number | null;
    backgroundDisplay?: string | null;
    // Carried through from the API frame metadata (not user-editable in the form).
    guardrailBlocked?: boolean | null;
    generationFailed?: boolean | null;
    replacedWithImage?: boolean | null;
  };
  vocamMetadata?: VocamMetadataType;
}

const schema = yup.object().shape(
  {
    id: yup.string().uuid().notRequired(),
    elearningId: yup.string().uuid().notRequired(),
    title: yup.string().max(255).trim().required('Slide Name is required'),
    isVideoFrame: yup.boolean(),
    content: yup.string().when('isVideoFrame', {
      is: false,
      then: () => yup.string().required('Content is required'),
    }),
    bgVideo: yup.string().when(['isVideoFrame', 'vocamMetadata'], {
      // Make bgVideo required if NO vocamMetadata
      is: (isVideoFrame: boolean, vocamMetadata: VocamMetadataType) =>
        !!isVideoFrame && !hasValidVocamDataIds(vocamMetadata),
      then: () =>
        yup
          .string()
          .required('Content is required')
          .matches(/\S/, 'Must have valid content'),
    }),
    bgVideoTitle: yup.string().when('bgVideoTitle', {
      is: (val: any) => !!val,
      then: () => yup.string().matches(/\S/, 'Must have valid content'),
      otherwise: () => yup.string().notRequired(),
    }),
    frameMetadata: yup.object().shape(
      {
        backgroundColor: yup
          .string()
          .matches(
            /(^#?([0-9a-f]{3}|[0-9a-f]{6})$)|(^$)/i,
            'Need to be valid Hexidecimal',
          )
          .notRequired()
          .nullable(),
        bgImage: yup.string().when('bgImage', {
          is: (val: any) => !!val,
          then: () => yup.string().matches(/\S/, 'Must have valid content'),
          otherwise: () => yup.string().notRequired().nullable(),
        }),
        bgVideo: yup.string().when('bgVideo', {
          is: (val: any) => !!val,
          then: () => yup.string().matches(/\S/, 'Must have valid content'),
          otherwise: () => yup.string().notRequired().nullable(),
        }),
        mute: yup.number().notRequired().nullable(),
        sound: yup.string().when('sound', {
          is: (val: any) => !!val,
          then: () => yup.string().matches(/\S/, 'Must have valid content'),
          otherwise: () => yup.string().notRequired().nullable(),
        }),
        swipeNavigation: yup.number().notRequired().nullable(),
        position: yup.number().notRequired(),
        disabled: yup.number().notRequired().nullable(),
        backgroundDisplay: yup.string().notRequired().nullable(),
        guardrailBlocked: yup.boolean().notRequired().nullable(),
        generationFailed: yup.boolean().notRequired().nullable(),
        replacedWithImage: yup.boolean().notRequired().nullable(),
      },
      [
        ['sound', 'sound'],
        ['bgVideo', 'bgVideo'],
        ['bgImage', 'bgImage'],
      ],
    ),
    vocamMetadata: yup
      .object()
      .shape({
        regionId: yup.number(),
        channelId: yup.number(),
        moduleId: yup.number(),
        topicId: yup.number(),
      })
      .when(['isVideoFrame', 'bgVideo'], {
        // Make vocamMetadata required if NO bgVideo
        is: (isVideoFrame: boolean, bgVideo: string) =>
          isVideoFrame && !bgVideo,
        then: () =>
          yup.object().shape({
            regionId: yup
              .number()
              .min(1, 'Region id need to be selected')
              .required('Region id need to be selected'),
            channelId: yup
              .number()
              .min(1, 'Channel id need to be selected')
              .required('Channel id need to be selected'),
            moduleId: yup
              .number()
              .min(1, 'Module id need to be selected')
              .required('Module id need to be selected'),
            topicId: yup
              .number()
              .min(1, 'Topic id need to be selected')
              .required('Topic id need to be selected'),
          }),
      }),
  },
  // We list cyclic dependency - so yup can handle circular dependency of these two properties w/o resulting into error
  [
    ['vocamMetadata', 'bgVideo'],
    ['bgVideoTitle', 'bgVideoTitle'],
  ],
);

interface SlideFormPropsType {
  currentFrame?: FrameDataType;
}

const SlideForm = ({
  currentFrame,
}: SlideFormPropsType): React.ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { elearningId, frameId } = useParams<ElearningParamType>();
  // AI slide actions require BOTH the LaunchDarkly rollout flag AND the company-level
  // "Allow AI Features" toggle (LMS2-5047).
  const { isPptConversionEnabled, isCreateWithAiEnabled } = useAiFeatureFlags();
  const [isVideoLibraryModalOpen, setIsVideoLibraryModalOpen] = useState(false);
  const [isMobileMode, setIsMobileMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [contentVersion, setContentVersion] = useState(0);
  const elearning = useGetElearningQuery(elearningId);
  const { data: framesData, refetch: refetchFrames } = useGetFramesQuery({
    elearningId,
  });

  useFrameStyle(currentFrame, isMobileMode);

  const formData = useMemo(() => {
    const data = getModuleFormData({
      currentElearningId: elearningId,
      currentFrameId: frameId,
      currentValue: currentFrame,
    });
    return {
      ...data,
      id: data.id || '', // Ensure id is never undefined
      isVideoFrame: data.isVideoFrame || false, // Ensure boolean
    };
  }, [currentFrame, elearningId, frameId]);

  const form = useForm<ContentFormType>({
    resolver: yupResolver(schema),
    values: formData, // Use values instead of defaultValues for reactive updates
  });

  const { handleSubmit, setValue } = form;

  // Reset per-slide state when the frame changes. hasUnsavedChanges lives on
  // this component, which does not remount between slides, so it must be
  // cleared here. A freshly-loaded slide starts clean.
  useEffect(() => {
    setIsMobileMode(false);
    setHasUnsavedChanges(false);
  }, [frameId]);

  const [updateFrameQuery, updateFrameStatus] = useUpdateFrameMutation();
  const [deleteFrameQuery, deleteFrameStatus] = useDeleteFrameMutation();
  const [duplicateFrameQuery, duplicateFrameStatus] =
    useDuplicateFrameMutation();
  const [createElementQuery] = useCreateElementMutation();
  const [tweakSlide] = useTweakSlideMutation();
  const [regeneratePptxSlide] = useRegeneratePptxSlideMutation();
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  // A timed-out edit may still land, so the open modal stops taking new submits until
  // it is closed; reopening it is a new request.
  const [isRegenerateLocked, setIsRegenerateLocked] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [isKeepAsImageModalOpen, setIsKeepAsImageModalOpen] = useState(false);
  const [activeSlideOp, setActiveSlideOp] = useState<SlideOp | null>(null);

  useEffect(() => {
    if (
      updateFrameStatus.isSuccess &&
      !updateFrameStatus.isError &&
      updateFrameStatus.data
    ) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: `Slide Saved Successfully`,
        },
      });

      // Reset unsaved changes state after successful save
      setHasUnsavedChanges(false);
    }

    if (updateFrameStatus.isError) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Something went wrong',
        },
      });
    }
  }, [updateFrameStatus, dispatch]);

  const onSubmit = async (data: ContentFormType) => {
    // Strip the placeholder markers so manually resolving a flagged/failed slide
    // clears them. Keep replacedWithImage: an image slide stays an image slide.
    const {
      guardrailBlocked: _,
      generationFailed: __,
      ...cleanMetadata
    } = data.frameMetadata || {};
    const frameDataToSubmit: Partial<FrameQueryType> = {
      ...data,
      frameMetadata: {
        ...cleanMetadata,
        backgroundColor: cleanMetadata.backgroundColor
          ? removeHashOnHex(cleanMetadata.backgroundColor)
          : '',
      },
      ...(isMobileMode && {
        mobileContent: data.mobileContent,
      }),
      templateId: localStorage.getItem('ELMOCA_FRAME_TEMPLATE') || null,
      // We do this optional attachment to payload to enable/disable BE trigger validation for vocam
      ...(!data.bgVideo
        ? {
            vocamMetadata: {
              channelId: data.vocamMetadata?.channelId,
              regionId: data.vocamMetadata?.regionId,
              moduleId: data.vocamMetadata?.moduleId,
              topicId: data.vocamMetadata?.topicId,
            },
          }
        : { vocamMetadata: {} }),
    };
    if (isMobileMode && currentFrame?.mobileContent !== data.mobileContent) {
      Dialog.confirm({
        withoutIcon: true,
        title: 'Save mobile changes',
        content:
          'Changes made to the content will only be saved on the mobile version of this slide. Are you sure you want to continue?',
        okLabel: 'Continue',
        cancelLabel: 'Cancel',
        onOk: async () => {
          await updateFrameQuery(frameDataToSubmit);
        },
        onCancel() {},
      });
    } else {
      await updateFrameQuery(frameDataToSubmit);
    }
  };

  const handleDuplicate = async () => {
    Dialog.confirm({
      title: 'Duplicate Slide',
      content:
        'You are about to leave this slide and duplicate everything onto a new slide. Please make sure you saved the changes first before duplicating',
      async onOk() {
        try {
          const originalElements = currentFrame?.elements || [];

          const { elements: _, ...frameWithoutElements } = currentFrame || {};
          const duplicateResult = await duplicateFrameQuery({
            ...frameWithoutElements,
            elearningId,
            frameMetadata: {
              ...currentFrame?.frameMetadata,
            },
            lcmsReferenceIds: undefined,
          }).unwrap();

          if (duplicateResult?.id && originalElements.length > 0) {
            try {
              const elementPromises = originalElements.map((element) =>
                createElementQuery({
                  name: element.name,
                  content: element.content,
                  elementMetadata: {
                    elementType: element.elementMetadata?.type,
                    width: element.elementMetadata?.width,
                    height: element.elementMetadata?.height,
                    position: element.elementMetadata?.position,
                  },
                  frameId: duplicateResult.id,
                }).unwrap(),
              );

              await Promise.all(elementPromises);

              dispatch({
                type: flashMessageActions.addFlashMessage,
                payload: {
                  isActive: true,
                  message: `Successfully duplicated slide`,
                },
              });

              history.push(
                generatePath(ModuleSlideRoutes.path, {
                  elearningId,
                  frameId: duplicateResult.id,
                }),
              );
            } catch (elementError) {
              dispatch({
                type: flashMessageActions.addFlashMessage,
                payload: {
                  isActive: true,
                  message:
                    'Slide duplicated but failed to copy tooltips. Please add them manually.',
                },
              });
            }
          } else if (duplicateResult?.id) {
            history.push(
              generatePath(ModuleSlideRoutes.path, {
                elearningId,
                frameId: duplicateResult.id,
              }),
            );

            dispatch({
              type: flashMessageActions.addFlashMessage,
              payload: {
                isActive: true,
                message: 'Slide duplicated successfully',
              },
            });
          }
        } catch (error) {
          dispatch({
            type: flashMessageActions.addFlashMessage,
            payload: {
              isActive: true,
              message: 'Failed to duplicate slide. Please try again.',
            },
          });
        }
      },
      onCancel() {},
    });
  };

  const handleDelete = () => {
    Dialog.danger({
      title: 'Delete Slide',
      content:
        'Deleting will remove all content from this slide and cannot be undone. Are you sure you want to delete this slide?',
      onOk: async () => {
        try {
          await deleteFrameQuery({
            id: frameId,
            content: `${currentFrame?.content}`,
          });

          history.replace(
            generatePath(ModuleRoutes.path, {
              elearningId,
            }),
          );

          dispatch({
            type: flashMessageActions.addFlashMessage,
            payload: {
              isActive: true,
              message: 'Slide has been deleted successfully',
            },
          });
        } catch (error) {
          dispatch({
            type: flashMessageActions.addFlashMessage,
            payload: {
              isActive: true,
              message: 'Failed to delete slide. Please try again.',
            },
          });
        }
      },
      onCancel() {},
    });
  };

  const handleBgVideoSelect = (currentFileName: string) => {
    setValue('bgVideo', currentFileName);
    // change topic to 0 or none
    setValue('vocamMetadata.topicId', 0);
  };

  const browseAssetLibraryHandler = () => {
    browseAssets(
      getCkFinderConfig({
        type: getCkFileType([CkFinderResourceTypeEnum.VIDEOS]),
        tenantId: elearning?.currentData?.tenantId ?? '',
        entityId: elearningId,
        folderName: 'elearnings',
      }),
      handleBgVideoSelect,
    );
  };

  const flashMessage = useCallback(
    (message: string) => {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: { isActive: true, message },
      });
    },
    [dispatch],
  );

  const navigateToFrame = useCallback(
    (newFrameId: string) => {
      history.push(
        generatePath(ModuleSlideRoutes.path, {
          elearningId,
          frameId: newFrameId,
        }),
      );
    },
    [history, elearningId],
  );

  const handleSlideOpError = useCallback(
    (
      err: any,
      fallbackMessage: string,
      { replacesInPlace = false }: { replacesInPlace?: boolean } = {},
    ) => {
      if (err?.data?.error === 'MONTHLY_CAP_EXCEEDED') {
        flashMessage(getMonthlyCapMessage(err?.data?.cap));
        return;
      }

      const highDemand = getHighDemandMessage(err);
      if (highDemand) {
        flashMessage(highDemand);
        return;
      }

      if (err?.data?.error === 'AI_CONCURRENCY_LIMIT') {
        flashMessage(AI_EDIT_BUSY_MESSAGE);
        return;
      }

      if (isSlideEditTimeout(err)) {
        flashMessage(getSlideEditTimeoutMessage(replacesInPlace));
        return;
      }

      flashMessage(fallbackMessage);
    },
    [flashMessage],
  );

  const isGuardrailBlocked = !!currentFrame?.frameMetadata?.guardrailBlocked;
  const isGenerationFailed = !!currentFrame?.frameMetadata?.generationFailed;
  // Empty placeholder from PPT import (blocked by guardrail OR generation failed
  // after retries) — same recovery flows, different explanation to the user.
  const isPlaceholder = isGuardrailBlocked || isGenerationFailed;
  const isReplacedWithImage = !!currentFrame?.frameMetadata?.replacedWithImage;

  const handleRegenerateSlide = async (
    mode: typeof REGENERATE_MODES.TWEAK | typeof REGENERATE_MODES.REGENERATE,
    userInstruction: string | undefined,
  ) => {
    if (!currentFrame) return;
    setActiveSlideOp(SLIDE_OPS.EDIT_AI);
    setRegenerateError(null);
    try {
      const newFrame =
        mode === REGENERATE_MODES.TWEAK
          ? await tweakSlide({
              elearningId,
              frameId: currentFrame.id,
              userInstruction: userInstruction!,
            }).unwrap()
          : await regeneratePptxSlide({
              elearningId,
              frameId: currentFrame.id,
              mode: 'regenerate',
              userInstruction,
            }).unwrap();

      setIsRegenerateModalOpen(false);
      await refetchFrames();
      flashMessage(
        "New slide generated. Compare and delete whichever you don't want.",
      );
      if (newFrame?.id) navigateToFrame(newFrame.id);
    } catch (err: any) {
      // A blocked or declined instruction stays in the modal so the admin can
      // revise it (LMS2-5106 rule 8). Wording provisional pending Design.
      const verdict = err?.status === 422 ? err?.data : null;
      if (verdict?.blocked) {
        setRegenerateError(BLOCKED_INSTRUCTION_MESSAGE);
        return;
      }
      if (verdict?.refused) {
        setRegenerateError(
          `AI declined: ${verdict.reason ?? 'Instruction is out of scope.'}`,
        );
        return;
      }
      // High demand and timeouts also stay in the modal: the admin acts on them from
      // here, and a toast would be gone before they look for it.
      const highDemand = getHighDemandMessage(err);
      if (highDemand) {
        setRegenerateError(highDemand);
        return;
      }
      if (isSlideEditTimeout(err)) {
        setIsRegenerateLocked(true);
        // Regenerating a placeholder rewrites it in place (a tweak always adds a copy).
        setRegenerateError(
          getSlideEditTimeoutMessage(
            mode === REGENERATE_MODES.REGENERATE && isPlaceholder,
          ),
        );
        return;
      }
      handleSlideOpError(err, 'Failed to regenerate slide. Please try again.');
    } finally {
      setActiveSlideOp(null);
    }
  };

  const handleKeepAsImage = async () => {
    if (!currentFrame) return;
    setActiveSlideOp(SLIDE_OPS.KEEP_AS_IMAGE);
    try {
      const newFrame = await regeneratePptxSlide({
        elearningId,
        frameId: currentFrame.id,
        mode: REGENERATE_MODES.KEEP_AS_IMAGE,
      }).unwrap();

      setIsKeepAsImageModalOpen(false);
      await refetchFrames();

      if (isPlaceholder) {
        if (newFrame?.content) {
          setValue('content', newFrame.content);
        }
        setContentVersion((v) => v + 1);
        flashMessage('Placeholder replaced with the original slide image.');
      } else {
        flashMessage(
          'New slide created with the original screenshot as an image.',
        );
        if (newFrame?.id) navigateToFrame(newFrame.id);
      }
    } catch (err: any) {
      handleSlideOpError(
        err,
        'Failed to create image slide. Please try again.',
        { replacesInPlace: isPlaceholder },
      );
    } finally {
      setActiveSlideOp(null);
    }
  };

  const isPptxImport =
    currentFrame?.frameMetadata?.importSource === 'pptx' &&
    !currentFrame?.isVideoFrame;
  const isAiGenerated =
    currentFrame?.frameMetadata?.importSource === 'ai' &&
    !currentFrame?.isVideoFrame;

  // Blocked slides only get "Replace with image"; replaced-with-image slides get neither.
  // Edit with AI keys off the source marker (LMS2-5108): manually built slides never get it.
  const showEditWithAi =
    !isGuardrailBlocked &&
    !isReplacedWithImage &&
    ((isAiGenerated && isCreateWithAiEnabled) ||
      (isPptxImport && isPptConversionEnabled));
  // "Replace with image" is a plain image copy (no AI), so it stays available for
  // any pptx-imported slide regardless of the AI toggle — otherwise a broken
  // placeholder from an earlier import could never be repaired (LMS2-5047).
  const showKeepAsImage = !isReplacedWithImage && isPptxImport;
  const allowRegenerate =
    !isGuardrailBlocked &&
    !isReplacedWithImage &&
    isPptxImport &&
    !!isPptConversionEnabled;

  return (
    <div>
      <NavigatePrompt canShowPrompt={hasUnsavedChanges} />
      <RegenerateSlideModal
        isOpen={isRegenerateModalOpen}
        isLoading={activeSlideOp === SLIDE_OPS.EDIT_AI}
        errorMessage={regenerateError}
        submitLocked={isRegenerateLocked}
        allowRegenerate={allowRegenerate}
        slideIndex={
          framesData ? framesData.findIndex((f) => f.id === frameId) : 0
        }
        onClose={() => {
          setIsRegenerateModalOpen(false);
          setRegenerateError(null);
          setIsRegenerateLocked(false);
        }}
        onSubmit={handleRegenerateSlide}
      />
      <KeepAsImageModal
        isOpen={isKeepAsImageModalOpen}
        isLoading={activeSlideOp === SLIDE_OPS.KEEP_AS_IMAGE}
        isGuardrailBlocked={isPlaceholder}
        currentFrame={currentFrame}
        onClose={() => setIsKeepAsImageModalOpen(false)}
        onConfirm={handleKeepAsImage}
      />
      {isPlaceholder && (
        <Alert
          type="warning"
          message=""
          isCloseable={true}
          id={
            isGuardrailBlocked
              ? 'guardrail-blocked-alert'
              : 'generation-failed-alert'
          }
          className="mb-3"
        >
          {isGuardrailBlocked ? (
            <>
              <strong>AI couldn&apos;t convert this slide.</strong> This slide
              contains sensitive subject matter. Your original slides are
              intact: use <strong>Replace with image</strong> to insert the
              original as a screenshot, or build the slide manually here.{' '}
            </>
          ) : (
            <>
              <strong>AI couldn&apos;t convert this slide.</strong> The
              conversion didn&apos;t succeed after several attempts — this is
              not a content issue. Your original slides are intact: use{' '}
              {showEditWithAi && (
                <>
                  <strong>Edit with AI</strong> to regenerate it,{' '}
                </>
              )}
              <strong>Replace with image</strong> to insert the original as a
              screenshot, or build the slide manually here.{' '}
            </>
          )}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link url={PPT_IMPORT_HELP_URL} newWindow>
            Learn more
          </Link>
        </Alert>
      )}
      <Content
        frameData={currentFrame}
        key={`${frameId}-${contentVersion}`}
        form={form}
        browseAssetLibraryHandler={browseAssetLibraryHandler}
        setIsVideoLibraryModalOpen={setIsVideoLibraryModalOpen}
        setIsMobileMode={setIsMobileMode}
        isMobileMode={isMobileMode}
        isPptxImport={isPptxImport}
        isAiGenerated={isAiGenerated}
        isGuardrailBlocked={isGuardrailBlocked}
        isGenerationFailed={isGenerationFailed}
        isReplacedWithImage={isReplacedWithImage}
        showEditWithAi={showEditWithAi}
        showReplaceWithImage={showKeepAsImage}
        activeSlideOp={activeSlideOp}
        onEditWithAi={() => {
          setIsRegenerateLocked(false);
          setRegenerateError(null);
          setIsRegenerateModalOpen(true);
        }}
        onReplaceWithImage={() => setIsKeepAsImageModalOpen(true)}
        onDirtyChange={setHasUnsavedChanges}
      />
      <ContainerForm maxColumns={1}>
        {currentFrame && (
          <>
            {!currentFrame.isVideoFrame && (
              <>
                <Tooltips />
                <SlideSetupComponent
                  frameData={currentFrame}
                  form={form}
                  key={frameId}
                />
              </>
            )}
            {!!currentFrame.isVideoFrame && (
              <VideoSlideSetupComponent
                isVideoLibraryModalOpen={isVideoLibraryModalOpen}
                setIsVideoLibraryModalOpen={setIsVideoLibraryModalOpen}
                browseAssetLibraryHandler={browseAssetLibraryHandler}
                frameData={currentFrame}
                form={form}
                key={`video-${frameId}`}
              />
            )}
          </>
        )}
        <Comments />
      </ContainerForm>
      <Row className="mt-4" justifyContent="start" isNoGutters={true}>
        <Col isAuto={true} grow={0}>
          <Button
            id="SlideFormSave"
            type="primary"
            htmlType="button"
            className="px-5"
            size="xl"
            isUppercase={false}
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Button>
        </Col>
        <Col isAuto={true}>
          <Popover mode="click" id="SlideFormPopover" isNoPadding={true}>
            <Popover.Target>
              <Button
                size="xl"
                icon={<MoreHorizIcon />}
                className="ml-2"
                htmlType="button"
                role="button"
              />
            </Popover.Target>
            <Popover.Content>
              <Button
                icon={<CopyIcon />}
                isText={true}
                isUppercase={false}
                onClick={handleDuplicate}
                htmlType="button"
                id="DuplicateFrame"
              >
                Duplicate Slide
              </Button>
              <Button
                icon={<DeleteOutlinedIcon />}
                isText={true}
                isUppercase={false}
                onClick={handleDelete}
                htmlType="button"
              >
                Delete Slide
              </Button>
            </Popover.Content>
          </Popover>
        </Col>
      </Row>
    </div>
  );
};

export default SlideForm;
